import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, CreditCard, Loader2, Sparkles, MessageSquareText } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const badge = (text, tone = "orange") => (
  <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-[0.2em] font-bold ${
    tone === "green" ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200"
      : tone === "red" ? "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200"
      : "bg-orange-600 text-white"
  }`}>{text}</span>
);

function PriceCard({ plan, interval, subscription, onSelect, loading }) {
  const price = interval === "year" ? plan.yearly_price : plan.monthly_price;
  const lookup = interval === "year" ? plan.yearly_lookup : plan.monthly_lookup;
  const isCurrent = subscription?.plan === plan.id && subscription?.billing_interval === interval;
  const savings = interval === "year" ? Math.round((plan.monthly_price * 12 - plan.yearly_price)) : 0;

  return (
    <div
      data-testid={`plan-card-${plan.id}-${interval}`}
      className={`border-2 p-6 flex flex-col ${
        isCurrent ? "border-orange-600 bg-orange-50 dark:bg-orange-950" : "border-border bg-card"
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground">Out &amp; In</div>
          <h3 className="font-heading text-3xl font-black tracking-tighter mt-1">{plan.name}</h3>
        </div>
        {isCurrent && badge("Current", "orange")}
        {plan.id === "unlimited" && !isCurrent && badge("Best value", "green")}
      </div>
      <div className="flex items-baseline gap-1 mb-1">
        <span className="font-heading text-5xl font-black tracking-tighter">${price}</span>
        <span className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
          / {interval === "year" ? "year" : "mo"}
        </span>
      </div>
      {interval === "year" && savings > 0 && (
        <div className="text-xs font-mono text-emerald-700 dark:text-emerald-400 mb-3">Save ${savings}/yr · 2 months free</div>
      )}
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mt-2 mb-3">
        {plan.seats_limit ? `${plan.seats_limit} users` : "Unlimited users"}
      </div>
      <ul className="space-y-2 mb-6 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm">
            <Check className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" /> <span>{f}</span>
          </li>
        ))}
      </ul>
      <Button
        onClick={() => onSelect(lookup)}
        disabled={loading || isCurrent}
        data-testid={`plan-select-${plan.id}-${interval}`}
        className={`w-full rounded-none h-11 uppercase tracking-[0.15em] text-xs font-bold ${
          isCurrent ? "bg-muted text-muted-foreground" : "bg-orange-600 hover:bg-orange-700 text-white"
        }`}
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> :
          isCurrent ? "Active plan" : (subscription?.status ? "Switch plan" : "Start 7-day trial")}
      </Button>
    </div>
  );
}

export default function Billing() {
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [interval, setInterval] = useState("month");
  const [busy, setBusy] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelDetail, setCancelDetail] = useState("");
  const nav = useNavigate();

  const load = async () => {
    try {
      const [{ data: p }, { data: s }] = await Promise.all([
        api.get("/billing/plans"),
        api.get("/billing/subscription"),
      ]);
      setPlans(p.plans || []);
      setSubscription(s);
    } catch (e) {
      toast.error("Couldn't load billing");
    }
  };
  useEffect(() => { load(); }, []);

  const startCheckout = async (lookup_key) => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };

  const requestPortal = () => {
    // Show the reason picker first so we can learn even if they don't cancel.
    setCancelReason("");
    setCancelDetail("");
    setCancelOpen(true);
  };

  const proceedToPortal = async () => {
    if (cancelReason) {
      try {
        await api.post("/billing/cancel-survey", { reason: cancelReason, detail: cancelDetail });
      } catch {
        // non-blocking — we still let them into the portal even if survey save fails
      }
    }
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal", {
        return_url: window.location.origin + "/billing",
      });
      if (data.portal_url) window.location.href = data.portal_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't open portal");
      setBusy(false);
    }
  };

  const statusTone = subscription?.status === "active" || subscription?.status === "trialing"
    ? "green" : subscription?.status ? "red" : "orange";
  const statusLabel = subscription?.grandfathered
    ? "Unlimited · complimentary"
    : subscription?.status === "trialing" ? "Trial"
    : subscription?.status === "active" ? "Active"
    : subscription?.status === "past_due" ? "Payment failed"
    : subscription?.status === "canceled" ? "Canceled"
    : subscription?.status === "none" || !subscription?.status ? "No plan yet"
    : subscription.status;

  if (!subscription) {
    return <div className="p-10 flex items-center gap-3 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading billing…
    </div>;
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="border-2 border-foreground p-2"><LogoMark size={44} className="text-foreground" /></div>
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">
            Out <span className="text-orange-600">&amp;</span> In · Billing
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mt-1">Plans &amp; billing</h1>
        </div>
      </div>

      <section className="border-2 border-border bg-card p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground mb-1">Current plan</div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="font-heading text-2xl font-black tracking-tight capitalize">
              {subscription.plan || "None"}
            </div>
            {badge(statusLabel, statusTone)}
            {subscription.billing_interval && badge(subscription.billing_interval === "year" ? "Annual" : "Monthly")}
          </div>
          <div className="text-xs text-muted-foreground mt-2">
            Seats: <strong>{subscription.seats_used}</strong>
            {subscription.seats_limit ? ` / ${subscription.seats_limit}` : " / unlimited"}
            {subscription.trial_end && <> · Trial ends {new Date(subscription.trial_end).toLocaleDateString()}</>}
            {subscription.current_period_end && !subscription.trial_end && (
              <> · Renews {new Date(subscription.current_period_end).toLocaleDateString()}</>
            )}
            {subscription.cancel_at_period_end && <> · <span className="text-red-600 font-bold">Cancels at period end</span></>}
          </div>
          {subscription.grandfathered && (
            <div className="text-xs text-emerald-700 dark:text-emerald-400 mt-2 flex items-center gap-1">
              <Sparkles className="h-3 w-3" /> Founding account — complimentary Unlimited access.
            </div>
          )}
        </div>
        {subscription.stripe_customer_id && (
          <Button
            onClick={requestPortal}
            disabled={busy}
            data-testid="billing-portal-btn"
            variant="outline"
            className="rounded-none border-2 h-11 uppercase tracking-[0.15em] text-xs font-bold"
          >
            <CreditCard className="h-3.5 w-3.5 mr-2" /> Manage payment
          </Button>
        )}
      </section>

      <div className="flex items-center gap-2 flex-wrap justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground">Pick a plan</div>
          <p className="text-sm text-muted-foreground mt-1">
            7-day free trial · Card required · Cancel any time from the payment portal.
          </p>
        </div>
        <div className="inline-flex border-2 border-border">
          <button
            type="button"
            data-testid="interval-month"
            onClick={() => setInterval("month")}
            className={`h-10 px-4 text-xs uppercase tracking-[0.15em] font-bold ${
              interval === "month" ? "bg-foreground text-background" : "text-muted-foreground"
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            data-testid="interval-year"
            onClick={() => setInterval("year")}
            className={`h-10 px-4 text-xs uppercase tracking-[0.15em] font-bold border-l-2 border-border ${
              interval === "year" ? "bg-foreground text-background" : "text-muted-foreground"
            }`}
          >
            Annual <span className="text-orange-600 ml-1">−2 mo</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {plans.map((p) => (
          <PriceCard key={p.id} plan={p} interval={interval} subscription={subscription} onSelect={startCheckout} loading={busy} />
        ))}
      </div>

      <div className="text-xs text-muted-foreground">
        Prices in USD. Applicable sales tax (US) or GST/VAT (elsewhere) is handled by Stripe at checkout.
        Need something custom? Reply to any dispatch email or reach out via <Link to="/company" className="underline">Company profile</Link>.
      </div>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent data-testid="cancel-survey-dialog" className="rounded-none border-2 sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <MessageSquareText className="h-4 w-4 text-orange-600" />
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground">Before you go</div>
            </div>
            <DialogTitle className="font-heading text-2xl font-black tracking-tight">
              Anything we could have done better?
            </DialogTitle>
            <DialogDescription>
              This is optional — but a one‑tap answer helps us fix the most common pain points fast. You'll head straight to Stripe after.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 pt-2">
            {[
              "Too expensive",
              "Missing a feature I need",
              "Not using it enough",
              "Found a better alternative",
              "Business changed / project ended",
              "Just updating my card",
              "Other",
            ].map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setCancelReason(r)}
                data-testid={`cancel-reason-${r.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`w-full text-left px-3 py-2 border-2 text-sm font-medium transition-colors ${
                  cancelReason === r
                    ? "border-orange-600 bg-orange-50 dark:bg-orange-950 text-foreground"
                    : "border-border hover:border-orange-600"
                }`}
              >
                {r}
              </button>
            ))}
            {(cancelReason === "Missing a feature I need" || cancelReason === "Other") && (
              <Textarea
                data-testid="cancel-detail"
                value={cancelDetail}
                onChange={(e) => setCancelDetail(e.target.value)}
                placeholder={cancelReason === "Missing a feature I need" ? "Which feature?" : "Tell us more (optional)"}
                rows={3}
                className="rounded-none border-2 font-mono text-sm mt-1"
              />
            )}
          </div>
          <DialogFooter className="pt-2 gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={proceedToPortal}
              disabled={busy}
              data-testid="cancel-skip"
              className="rounded-none border-2 h-10 uppercase tracking-[0.15em] text-xs font-bold flex-1"
            >
              Skip &amp; open portal
            </Button>
            <Button
              type="button"
              onClick={proceedToPortal}
              disabled={busy || !cancelReason}
              data-testid="cancel-submit"
              className="rounded-none bg-orange-600 hover:bg-orange-700 text-white h-10 uppercase tracking-[0.15em] text-xs font-bold flex-1"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send & continue"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
